import React from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";

import { FormatAmount } from "../../../utils/Formatter";
import {
  calculateAmount,
  getPaymentFamilyFromPathname
} from "../../../utils/SurchargeUtils";
import { PaymentFamily } from "../../../model/Charge";
import "./PaymentInfo.css";
import TestIndicator from "../../common/TestIndicator";
import SurchargePopover from "../../common/SurchargePopover";

const useStyles = makeStyles(({ palette }) => {
  return {
    root: {
      color: palette.primary.contrastText,
      background: `linear-gradient(36deg, ${palette.primary.dark} 0%, ${palette.primary.main} 54%, ${palette.primary.light} 100%)`,
      padding: "25px",
      alignItems: "center",
      display: "flex",
      position: "relative",
      fontSize: "1.15rem"
    }
  };
});

const getLogo = charge => {
  const { theme } = charge;
  return (
    (theme && theme.logo) ||
    (window.location.hostname.indexOf("lyra") < 0 &&
      theme &&
      theme.domainName &&
      "alt_default") ||
    "default"
  );
};

const PaymentInfo = ({ charge }) => {
  const location = useLocation();
  const classes = useStyles();
  const {
    due,
    paymentFamilies,
    currency,
    shopName,
    orderId,
    testMode,
    feeRenderMode
  } = charge;

  const totalAmount = calculateAmount(
    due,
    location.pathname,
    paymentFamilies.surcharges,
    feeRenderMode
  );

  const formatedTotalAmount = FormatAmount(totalAmount, currency);
  let surchargeAdditionalMention = <div>Convenience charges may apply</div>;
  const paymentFamily = getPaymentFamilyFromPathname(location.pathname);
  if (PaymentFamily.UNKNOWN !== paymentFamily) {
    if (feeRenderMode && feeRenderMode === "EXCLUSIVE") {
      surchargeAdditionalMention = <div>Convenience charges may apply</div>;
    } else {
      surchargeAdditionalMention = (
        <div>
          {`Inclusive of ${FormatAmount(
            totalAmount - due,
            currency
          )} (convenience fee + GST)`}
        </div>
      );
    }
  }

  return (
    <div className={classes.root}>
      <div className="shop-logo-container">
        {getLogo(charge) === "alt_default" ? (
          <img
            src={`/images/error-logos/${charge.theme.domainName.image}_logo.png`}
            alt="Logo"
          />
        ) : (
          <img src={`/static/shop-logo/${getLogo(charge)}`} alt="Logo" />
        )}
      </div>
      <div style={{ minWidth: "200px" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div id="shopName">{shopName}</div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div id="orderId">{`#${orderId}`}</div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div id="amount">{formatedTotalAmount}</div>
        </div>
        {paymentFamilies.fees && paymentFamilies.fees.length > 0 && (
          <div id="surcharge-details-container">
            {surchargeAdditionalMention}
            <SurchargePopover charge={charge} />
          </div>
        )}
        {testMode && <TestIndicator />}
      </div>
    </div>
  );
};

PaymentInfo.propTypes = {
  charge: PropTypes.shape({
    currency: PropTypes.string.isRequired,
    due: PropTypes.number.isRequired,
    feeRenderMode: PropTypes.string,
    shopId: PropTypes.string.isRequired,
    shopName: PropTypes.string.isRequired,
    shopLogoId: PropTypes.string,
    orderId: PropTypes.string.isRequired,
    testMode: PropTypes.bool.isRequired,
    paymentFamilies: PropTypes.shape({
      surcharges: PropTypes.arrayOf(PropTypes.shape()),
      fees: PropTypes.arrayOf(PropTypes.shape())
    }),
    theme: PropTypes.shape({
      domainName: PropTypes.shape({
        image: PropTypes.string
      })
    }).isRequired
  }).isRequired
};

export default PaymentInfo;
